/**
 * Shapes mirrored from backend `admin-booking` module.
 */
export type VendorModuleType =
  | 'VET'
  | 'WALKER'
  | 'TRAINER'
  | 'GROOMER'
  | 'CAFE'
  | 'RESTAURANT'
  | 'FOOD'
  | 'PHARMACY'
  | 'LAB'
  | 'CLINIC'
  | 'CREMATION'
  | 'ADOPTION';

export type BookingStatus =
  | 'PENDING_PAYMENT'
  | 'AWAITING_VENDOR'
  | 'CONFIRMED'
  | 'IN_PROGRESS'
  | 'COMPLETED'
  | 'CANCELLED'
  | 'NO_SHOW';

export const BOOKING_STATUS_OPTIONS: BookingStatus[] = [
  'PENDING_PAYMENT',
  'AWAITING_VENDOR',
  'CONFIRMED',
  'IN_PROGRESS',
  'COMPLETED',
  'CANCELLED',
  'NO_SHOW',
];

export const MODULE_TYPE_OPTIONS: VendorModuleType[] = [
  'VET',
  'WALKER',
  'TRAINER',
  'GROOMER',
  'CAFE',
  'RESTAURANT',
  'FOOD',
  'PHARMACY',
  'LAB',
  'CLINIC',
  'CREMATION',
  'ADOPTION',
];

export type BookingListItem = {
  id: string;
  status: BookingStatus;
  module_type: VendorModuleType;
  vendor_profile_id: string;
  vendor_business_name: string | null;
  user_id: string;
  user_name: string | null;
  /** OTP-only accounts have no name, so this is usually the only identifier. */
  user_phone: string | null;
  slot_start: string;
  slot_end: string;
  price_inr: string;
  payment_status: string | null;
  created_at: string;
  /** What was booked, not just which module it belongs to. */
  service_name: string | null;
  pet: BookingPet | null;
  /**
   * Slot has ended with the booking still open — nobody closed it out.
   *
   * Computed server-side from the same rule the `needs_attention` filter uses,
   * so the badge and the queue can never disagree.
   */
  needs_attention: boolean;
  incident_count: number;
  open_incident_count: number;
  support_request_count: number;
  /** Status changes after the first — a rough measure of how contested it is. */
  reschedule_count: number;
  /**
   * CAFE/RESTAURANT only; null for every other module.
   *
   * A dining booking is a party of people at a table rather than a pet at an
   * appointment. One of the seventeen in this data has a pet; all seventeen have
   * a party size — so the row has to lead with the party, not the animal.
   */
  dining_offering: DiningOffering | null;
  party_size: number | null;
};

export type DiningBookingMode = 'TABLE_RESERVATION' | 'ENTRY_PASS' | 'EVENT';
export type DiningPriceUnit = 'FLAT' | 'PER_GUEST' | 'PER_PET';

export type DiningOffering = {
  mode: DiningBookingMode;
  price_unit: DiningPriceUnit;
  /** How long the table is held, in minutes. */
  turn_minutes: number | null;
  capacity: number;
  min_party: number;
  max_party: number;
};

/** What the vendor is actually selling, said as a person would. */
export const DINING_MODE_LABELS: Record<DiningBookingMode, string> = {
  TABLE_RESERVATION: 'Table reservation',
  ENTRY_PASS: 'Entry pass',
  EVENT: 'Event ticket',
};

export const DINING_PRICE_UNIT_LABELS: Record<DiningPriceUnit, string> = {
  FLAT: 'flat rate',
  PER_GUEST: 'per guest',
  PER_PET: 'per pet',
};

/** Columns the API can sort by — mirrors BOOKING_SORT_FIELDS on the backend. */
export const BOOKING_SORT_FIELDS = [
  'slot_start',
  'created_at',
  'price_inr',
  'status',
  'module_type',
] as const;

/** Statuses that mean the booking is still somebody's job. */
export const OPEN_BOOKING_STATUSES: BookingStatus[] = [
  'AWAITING_VENDOR',
  'CONFIRMED',
  'IN_PROGRESS',
];

/** How a status reads to a person, rather than as an enum. */
export const BOOKING_STATUS_LABELS: Record<BookingStatus, string> = {
  PENDING_PAYMENT: 'Awaiting payment',
  AWAITING_VENDOR: 'Awaiting vendor',
  CONFIRMED: 'Confirmed',
  IN_PROGRESS: 'In progress',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
  NO_SHOW: 'No show',
};

/** Tiles on the list, each of which is also a filter it can apply. */
export type BookingCounts = {
  total: number;
  needs_attention: number;
  awaiting_vendor: number;
  in_progress: number;
  today: number;
  upcoming: number;
  open_incidents: number;
  open_support_requests: number;
  captured_inr: string;
  refunded_inr: string;
};

export type BookingListResponse = {
  items: BookingListItem[];
  total: number;
  page: number;
  page_size: number;
};

export type BookingAudit = {
  id: string;
  from_status: BookingStatus | null;
  to_status: BookingStatus;
  actor_type: string;
  actor_id: string | null;
  reason: string | null;
  created_at: string;
};

export type BookingPet = {
  id: string;
  name: string;
  species: string;
};

export type BookingPickup = {
  address: string;
  landmark: string | null;
  pincode: string | null;
  contact_phone: string | null;
  access_notes: string | null;
};

export type WalkOtpStatus = {
  start_verified_at: string | null;
  end_verified_at: string | null;
  end_bypassed: boolean;
  end_bypass_reason: string | null;
};

/** Deceased-pet snapshot on a CREMATION booking (null for other modules). */
export type DeceasedPet = {
  name: string | null;
  species: string | null;
  weight_kg: string | null;
};

export type CremationFulfillmentStatus =
  | 'CONFIRMED'
  | 'PICKED_UP'
  | 'CREMATION_IN_PROGRESS'
  | 'ASHES_READY'
  | 'ASHES_RETURNED';

/** Cremation fulfillment journey on a CREMATION booking (null until accepted). */
export type CremationOversight = {
  status: CremationFulfillmentStatus;
  confirmed_pickup_at: string | null;
  picked_up_at: string | null;
  cremation_started_at: string | null;
  ashes_ready_at: string | null;
  ashes_returned_at: string | null;
  certificate_number: string | null;
  cremated_at: string | null;
};

export type LabFulfillmentStatus = 'CONFIRMED' | 'SAMPLE_COLLECTED' | 'PROCESSING' | 'REPORT_READY';

/** Lab fulfillment journey on a LAB booking (null until accepted). Read-only oversight. */
export type LabOversight = {
  status: LabFulfillmentStatus;
  confirmed_at: string | null;
  sample_collected_at: string | null;
  processing_at: string | null;
  report_ready_at: string | null;
  report_notes: string | null;
  reported_at: string | null;
  report_documents: { id: string; thumb_path: string; full_path: string }[];
};

export type WalkIncident = {
  id: string;
  type: string;
  severity: string;
  note: string;
  status: string;
  resolution_note: string | null;
  resolved_at: string | null;
  owner_acknowledged_at: string | null;
  created_at: string;
  photos: { id: string; thumb_path: string; full_path: string }[];
};

export type BookingDetail = BookingListItem & {
  service_id: string;
  /** The service/package name booked (e.g. a grooming package tier's underlying service). */
  service_name: string;
  pet_id: string | null;
  pet: BookingPet | null;
  /** Set when this walk was generated by a WalkPlan (subscription child). */
  walk_plan_id: string | null;
  /** Set when this session was generated by a TrainingPackage (course child). */
  training_package_id: string | null;
  /** Walker/trainer at-location visit context — present for WALKER/TRAINER bookings. */
  pickup: BookingPickup | null;
  /** Walk-session OTP verification state — present for all bookings (meaningful for WALKER). */
  walk_otp: WalkOtpStatus;
  /** Walker-reported incidents on this walk (WALKER only; empty otherwise). */
  incidents: WalkIncident[];
  /** Deceased-pet snapshot (CREMATION only; null otherwise). */
  deceased_pet: DeceasedPet | null;
  /** Cremation fulfillment journey (CREMATION only; null until accepted). */
  cremation: CremationOversight | null;
  /** Lab fulfillment journey (LAB only; null until accepted). */
  lab: LabOversight | null;
  /** Dining table reservations only; null elsewhere. Explains the total. */
  party_size: number | null;
  notes: string | null;
  cancelled_by: string | null;
  cancel_reason: string | null;
  updated_at: string;
  audits: BookingAudit[];
};

export type BookingListFilters = {
  q?: string;
  /** Several statuses at once, comma-separated. Beats `status` when both are set. */
  statuses?: string;
  /** Several modules at once, comma-separated. */
  modules?: string;
  /** The SLOT window, which is a different question from `from`/`to` (created). */
  slot_from?: string;
  slot_to?: string;
  /** '' = either, 'true' / 'false' = that side only. Matches the URL tri-state. */
  needs_attention?: string;
  has_incident?: string;
  has_support_request?: string;
  payment_status?: string;
  page?: number;
  page_size?: number;
  status?: BookingStatus;
  module_type?: VendorModuleType;
  /** Inclusive created-date range, YYYY-MM-DD. */
  from?: string;
  to?: string;
  /** Narrow to one vendor. */
  vendor_profile_id?: string;
  /** Narrow to one customer — linked from the customer detail page. */
  user_id?: string;
};
